import { isDevelopment } from 'src/module/helpers';
import api from './api';
import firebaseService, { getNotificationPermission, isWebView } from './firebase';
import Bowser from 'bowser';

const isWebViewApp = isWebView
/**
 * Mengambil identitas perangkat/browser.
 * UUID tetap sama meskipun user logout atau berganti akun
 * pada browser yang sama.
 */

const DEVICE_UID_KEY = 'sks_device_uid'

const isValidUUID = (value) => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value
    )
}

/**
 * Mendapatkan identitas perangkat berdasarkan platform.
 *
 * WebView Android : session_android_<UUID>
 * Browser biasa   : session_<UUID>
 *
 * ID tetap sama setelah logout dan login kembali.
 */
export const getDeviceUid = (isWebView = false) => {
    if (typeof window === 'undefined') {
        throw new Error('getDeviceUid() hanya dapat dipanggil di browser')
    }

    const storageKey = isWebView
        ? `${DEVICE_UID_KEY}_android`
        : DEVICE_UID_KEY

    try {
        const existingUid = localStorage.getItem(storageKey)

        if (existingUid) {
            const prefix = isWebView
                ? 'session_android_'
                : 'session_'

            const storedUuid = existingUid.startsWith(prefix)
                ? existingUid.slice(prefix.length)
                : ''

            if (isValidUUID(storedUuid)) {
                return existingUid
            }
        }

        const uuid = crypto.randomUUID()

        const deviceUid = isWebView
            ? `session_android_${uuid}`
            : `session_${uuid}`

        localStorage.setItem(storageKey, deviceUid)

        return deviceUid
    } catch {
        throw new Error(
            'Tidak dapat membaca atau menyimpan identitas perangkat'
        )
    }
}


/**
 * Menghapus identitas perangkat hanya jika memang diperlukan,
 * misalnya saat pengguna meminta reset identitas browser.
 * Jangan panggil fungsi ini saat logout biasa.
 */
export const clearDeviceUid = () => {
    if (typeof window === 'undefined') {
        return
    }
    window.localStorage.removeItem(DEVICE_UID_KEY)
}



class DeviceService {
    constructor() {
        this.currentSession = null;
        this.isDeviceRegistered = false;
    }

    /**
     * Initialize device registration flow
     */

    async initializeDevice(userId) {
        const startedAt = Date.now();
        const traceId = `DEVICE-${startedAt}`;

        const log = (stage, details = {}) => {
            if (!isDevelopment) return;

            console.log(
                `[DEVICE][${traceId}][${stage}]`,
                {
                    timestamp: new Date().toISOString(),
                    elapsedMs: Date.now() - startedAt,
                    ...details,
                }
            );
        };

        const logError = (stage, error, details = {}) => {
            if (!isDevelopment) return;

            console.error(
                `[DEVICE][${traceId}][${stage}]`,
                {
                    timestamp: new Date().toISOString(),
                    elapsedMs: Date.now() - startedAt,
                    ...details,
                    error: {
                        name: error?.name,
                        message: error?.message || String(error),
                        stack: error?.stack,
                    },
                }
            );
        };

        try {
            log("START", {
                userId: userId ?? null,
                isWebView: Boolean(isWebViewApp),
            });

            // 1. Validasi user ID
            if (
                userId === undefined ||
                userId === null ||
                userId === ""
            ) {
                log("ABORT_INVALID_USER", {
                    reason: "missing_user_id",
                });

                return {
                    success: false,
                    reason: "missing_user_id",
                    message: "User ID tidak tersedia",
                };
            }

            // 2. Ambil FCM token
            let token = null;

            try {
                log("TOKEN_FETCH_START", {
                    source: isWebViewApp
                        ? "android_webview"
                        : "firebase_web",
                });

                token = isWebViewApp
                    ? localStorage.getItem("fcm_token_android")
                    : await firebaseService.getToken();

                log("TOKEN_FETCH_RESULT", {
                    hasToken: Boolean(token),
                    tokenLength:
                        typeof token === "string"
                            ? token.length
                            : 0,
                });
            } catch (error) {
                logError("TOKEN_FETCH_ERROR", error);

                return {
                    success: false,
                    reason: "token_fetch_failed",
                    message: "Gagal mengambil FCM token",
                    error: error?.message || String(error),
                };
            }

            if (!token || typeof token !== "string") {
                log("ABORT_NO_TOKEN", {
                    reason: "no_token",
                });

                return {
                    success: false,
                    reason: "no_token",
                    message: "FCM token tidak tersedia",
                };
            }

            // 3. Login perangkat untuk akun aktif
            log("LOGIN_DEVICE_START", {
                userId,
            });

            let loginResponse;

            try {
                loginResponse = await this.loginToDevice(
                    userId,
                    token
                );
            } catch (error) {
                logError("LOGIN_DEVICE_EXCEPTION", error, {
                    userId,
                });

                return {
                    success: false,
                    reason: "login_device_exception",
                    message: "Gagal melakukan login perangkat",
                    error: error?.message || String(error),
                };
            }

            // Jangan mencatat token atau seluruh respons API.
            log("LOGIN_DEVICE_RESPONSE", {
                userId,
                responseType: typeof loginResponse,
                hasResponse: Boolean(loginResponse),
                responseKeys:
                    loginResponse &&
                        typeof loginResponse === "object"
                        ? Object.keys(loginResponse)
                        : [],
                success: loginResponse?.success,
                message: loginResponse?.message,
                reason: loginResponse?.reason,
                hasData: Boolean(loginResponse?.data),
            });

            // 4. Validasi respons backend
            if (!loginResponse?.success) {
                const reason =
                    loginResponse?.reason ||
                    "login_device_failed";

                const response = {
                    success: false,
                    reason,
                    message:
                        loginResponse?.message ||
                        "Registrasi atau login perangkat gagal",
                };

                log("LOGIN_DEVICE_FAILED", {
                    userId,
                    reason: response.reason,
                    message: response.message,
                });

                // Jangan mengubah status perangkat menjadi aktif.
                return response;
            }

            // 5. Validasi session dan data perangkat
            const responseData = loginResponse?.data?.data;
            const session = responseData?.session;
            const device = responseData?.device;

            if (!session?.id || !device?.id) {
                log("INVALID_DEVICE_RESPONSE", {
                    userId,
                    hasSession: Boolean(session),
                    hasSessionId: Boolean(session?.id),
                    hasDevice: Boolean(device),
                    hasDeviceId: Boolean(device?.id),
                });

                return {
                    success: false,
                    reason: "invalid_device_response",
                    message:
                        "Login berhasil, tetapi data session atau device tidak lengkap",
                };
            }

            // 6. Simpan session perangkat
            try {
                localStorage.setItem(
                    "device_session",
                    JSON.stringify({
                        sessionId: session.id,
                        deviceId: device.id,
                        loggedInAt: new Date().toISOString(),
                    })
                );
            } catch (error) {
                logError("SAVE_DEVICE_SESSION_FAILED", error, {
                    userId,
                });

                return {
                    success: false,
                    reason: "save_device_session_failed",
                    message: "Gagal menyimpan session perangkat",
                    error: error?.message || String(error),
                };
            }

            // 7. Perbarui status hanya setelah login berhasil
            this.isDeviceRegistered = true;
            this.currentSession = session;

            log("DEVICE_INITIALIZED", {
                userId,
                deviceId: device.id,
                sessionId: session.id,
            });

            log("COMPLETE", {
                success: true,
                userId,
                totalElapsedMs: Date.now() - startedAt,
            });

            return {
                ...loginResponse,
                success: true,
            };
        } catch (error) {
            logError("INITIALIZE_DEVICE_EXCEPTION", error, {
                userId: userId ?? null,
            });

            return {
                success: false,
                reason: "initialize_device_exception",
                message:
                    error?.message ||
                    "Gagal menginisialisasi perangkat",
                error: error?.message || String(error),
            };
        }
    }


    /**
     * Register device ke backend
     */
    async registerDevice(fcmToken) {
        try {
            const response = await api.post('/multi-device/register', {
                fcmToken,
                deviceIdentifier: this.getDeviceIdentifier(),
                userAgent: navigator.userAgent,
                appVersion: process.env.REACT_APP_VERSION || '1.0.0'
            });

            if (isDevelopment)
                console.log('[DEVICE] Device registered:', isWebView ? JSON.stringify(response.data) : response.data);

            return {
                success: true,
                data: response.data,
                deviceId: response.data.deviceId
            };

        } catch (error) {
            if (isDevelopment)
                console.error('[DEVICE] Register failed:', JSON.stringify(error));

            // Handle specific errors
            if (error.response?.status === 409) {
                // Device already registered
                return {
                    success: true,
                    message: 'Device already registered',
                    data: error.response.data
                };
            }

            return {
                success: false,
                message: 'Failed to register device',
                error: error.response?.data || error.message
            };
        }
    }

    /**
     * Login user ke device
     */
    async loginToDevice(userId, fcmToken) {
        try {
            const sessionId = this.generateSessionId(userId);

            const response = await api.post('/multi-device/login', {
                fcmToken,
                sessionId,
                deviceInfo: this.getDeviceInfo(),
                userAgent: navigator.userAgent
            });

            if (isDevelopment)
                console.log('[DEVICE] User logged into device:', isWebView ? JSON.stringify(response.data) : response.data);

            return {
                success: true,
                data: response.data
            };

        } catch (error) {
            if (isDevelopment)
                console.error('[DEVICE] Login failed:', error);

            return {
                success: false,
                message: 'Failed to login to device',
                error: error.response?.data || error.message
            };
        }
    }

    /**
     * Logout user dari device
     */
    async logoutFromDevice(user) {
        try {
            // console.log(user)
            const isLoggedIn = user?.deviceStatus?.isLoggedIn
            if (!isLoggedIn) return {
                success: false, message: "Device not login"
            }

            // const hasFcmToken = user?.deviceStatus?.hasFcmToken
            const fcmToken = user?.deviceStatus.firebaseState.token

            if (!fcmToken) {
                return { success: false, message: 'No FCM token found' };
            }

            const response = await api.post('/multi-device/logout', {
                fcmToken,
                sessionId: user?.deviceStatus?.session?.sessionId,
                deviceId: user?.deviceStatus?.session?.deviceId
            });

            if (isDevelopment)
                console.log('[DEVICE] User logged out from device');

            // Clear local session
            this.clearLocalSession();

            return {
                success: true,
                data: response.data
            };

        } catch (error) {
            if (isDevelopment)
                console.error('[DEVICE] Logout failed:', error);

            // Force clear local session even if API fails
            this.clearLocalSession();

            return {
                success: false,
                message: 'Logout failed but local session cleared',
                error: error.response?.data || error.message
            };
        }
    }

    /**
     * Get current device session
     */
    getCurrentSession() {
        try {
            const sessionStr = localStorage.getItem('device_session');
            return sessionStr ? JSON.parse(sessionStr) : null;
        } catch {
            return null;
        }
    }

    /**
     * Clear local session data
     */
    clearLocalSession() {
        localStorage.removeItem('device_session');
        this.currentSession = null;
        this.isDeviceRegistered = false;

        // Clear FCM token
        firebaseService.clearToken();

        console.log('[DEVICE] Local session cleared');
    }

    /**
     * Get device info untuk tracking
     */
    getDeviceInfo() {
        const parser = Bowser.getParser(navigator.userAgent);

        return {
            device_ui: this.getDeviceIdentifier(),
            browser: parser.getBrowserName() || 'Unknown',
            browserVersion: parser.getBrowserVersion() || 'Unknown',
            os: parser.getOSName() || 'Unknown',
            osVersion: parser.getOSVersion() || 'Unknown',

            screenSize: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            language: navigator.language,
            userAgent: navigator.userAgent,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            platform: navigator.platform,
            cookiesEnabled: navigator.cookieEnabled,
            doNotTrack: navigator.doNotTrack || 'unspecified',
            hardwareConcurrency: navigator.hardwareConcurrency,
            deviceMemory: navigator.deviceMemory || 'unknown',
            connection: navigator.connection ? {
                effectiveType: navigator.connection.effectiveType,
                downlink: navigator.connection.downlink,
                rtt: navigator.connection.rtt
            } : null
        };
    }

    /**
     * Generate unique device identifier
     */
    getDeviceIdentifier() {
        // Try to get from localStorage first
        let deviceId = getDeviceUid();
        return deviceId;
    }

    /**
     * Generate session ID
     */
    generateSessionId(userId) {
        if (isWebView) {
            return `session_android_${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        return `session_${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Check if device is registered and logged in
     */
    isLoggedIn() {
        const session = this.getCurrentSession();
        const fcmToken = isWebView ? localStorage?.getItem("fcm_token_android") : localStorage.getItem('fcm_token');
        return !!(session && fcmToken);
    }

    /**
     * Get device status summary
     */
    getStatus() {
        return {
            isRegistered: this.isDeviceRegistered,
            isLoggedIn: this.isLoggedIn(),
            hasFcmToken: isWebView ? !!localStorage?.getItem("fcm_token_android") : !!localStorage.getItem('fcm_token'),
            session: this.getCurrentSession() || {},
            firebaseState: firebaseService.getState()
        };
    }

    /**
     * Handle app start - auto initialize device
     */
    async handleAppStart(userId) {
        // Cek permission status
        const permission = getNotificationPermission();

        if (permission === 'granted') {
            // Auto initialize jika permission sudah diberikan
            return this.initializeDevice(userId);
        } else if (permission === 'default') {
            // Permission belum diminta, tunggu user action
            if (isDevelopment)
                console.log('[DEVICE] Waiting for user to grant permission');
            return { success: false, reason: 'permission_needed' };
        } else {
            // Permission denied
            if (isDevelopment)
                console.log('[DEVICE] Permission denied by user');
            return { success: false, reason: 'permission_denied' };
        }
    }
}

// Singleton instance
const deviceService = new DeviceService();
export default deviceService;